import { Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { AppStoreDispatcher } from '../state/app.state.dispatcher';
import { CollectionReference, doc, Firestore, getDoc, getDocs, query, setDoc, where, collection, DocumentReference, Query } from '@angular/fire/firestore';
import { FriendRequestStatusType } from './friendRequests.service';
import { ProfileStateDispatcher } from '../state/profile/profile.dispatcher';
import { take } from 'rxjs/operators';
import { ApiService } from '../backend/api.service';

@Injectable({
    providedIn: 'root'
})
export class FriendsListService {

    constructor(
        public cloudFNs: Functions,
        public appStateDispatch: AppStoreDispatcher,
        public profileDispatch: ProfileStateDispatcher,
        public firestore: Firestore,
        public apiService: ApiService,
        ) {}

    /**
     * Load user's friends list
     */
    getUsers(userID: string) {
        return doc(this.firestore, `users/${userID}`) as DocumentReference<any>;
    }

    public async loadFriendsList(): Promise < any > {
        const myID = this.profileDispatch.getMyID();
        const collectionReference = collection(this.firestore, `users/${myID}/contacts`) as Query<any>;
        const collectionQuery = query(collectionReference, where('status', '==', FriendRequestStatusType.ACCEPTED)) as Query<any>;
        return getDocs(collectionQuery)
            .then(querySnapshot => {
                const promises: any[] = [];
                querySnapshot.forEach(doc => {
                    const userID = doc.data().userID;
                    const conversationID = doc.data().conversationID;
                    promises.push(
                        getDoc(this.getUsers(userID)).then(doc => {
                            return { ...doc.data(), conversationID };
                        })
                    );
                });
                return Promise.all(promises).then(friends => {
                    return friends;
                });
            })
            .catch(error => {
                return {
                    success: false,
                    error: error
                };
            });
    }

    /**
     * Search the entire users list by query(displayName key)
     * @param query
     */
    public async searchFriendsList(query: string): Promise < any > {
        const myID = this.profileDispatch.getMyID();
        const collectionQuery = collection(this.firestore, `users`) as Query<any>;
        return getDocs(collectionQuery).then(querySnapshot => {
            const collectionQuery = collection(this.firestore, `users/${myID}/contacts`) as Query<any>;
            return getDocs(collectionQuery).then(contactDocs => {
                const friends: any[] = [];
                querySnapshot.forEach(doc => {
                    // doc.data() is never undefined for query doc snapshots
                    const inputMatchesName = doc.data().displayName.toLowerCase().indexOf(query.toLowerCase()) != -1;
                    const isGuest = doc.data().displayName.toLowerCase().indexOf('guest') === 0;
                    const isClient = doc.data().uid === myID;
                    if (inputMatchesName && !isGuest && !isClient) {
                        let status = null; // FriendRequestStatusType
                        for (const contactDoc of contactDocs.docs) {
                            if (doc.data().uid == contactDoc.data().userID) {
                                status = contactDoc.data().status;
                                break;
                            }
                        }
                        // console.log(doc.id, " => ", doc.data());
                        friends.push({ ...doc.data(), status });
                    }
                });
                return friends;
            });
        });
    }

    getUserContacts(userID: string, myID: string) {
        return doc(this.firestore, `users/${userID}/contacts/${myID}`);
    }

    /**
     * Send friend request to user
     * @param userID The user's uid
     */
    public async sendFriendRequest(userID): Promise < any > {
        const myID = await this.profileDispatch.getMyID();
        return Promise.all([
            // Append friend request info to user's node
            setDoc(this.getUserContacts(userID, myID), {
                userID: myID,
                status: FriendRequestStatusType.FOLLOWING
            }),
            // Append friend request info to my node
            setDoc(this.getUserContacts(myID, userID), {
                userID: userID,
                status: FriendRequestStatusType.PENDING
            })
        ]).then(() => {
            this.apiService.sendFriendRequest({ senderID: myID, friendID: userID }).then(result => {
                // Read result of the Cloud Function.
                console.log(result);
            }).catch(error => {
                // Getting the Error details.
                const code = error.code;
                const message = error.error;
                const details = error.details;
                console.log(error);
            });
        }).catch(error => {
            console.error(error);
            return {
                success: false,
                error: error
            };
        });
    }

}
